import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";

require("./assets/css/Video.css");

const VideoDepartmentUsersModal = ({ show, onHide, department, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        if (department?.id) {
            fetchDepartmentUsers();
            fetchAvailableUsers();
        }
    }, [department]);

    const fetchDepartmentUsers = async () => {
        try {
            setLoading(true);

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/departments/users-list`,
                { department_id: department.id },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setUsers(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableUsers = async () => {
        try {
            // Buscar usuários que podem ser adicionados ao departamento
            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/departments/users-list-selected`,
                { department_id: department.id },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setAvailableUsers(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar usuários disponíveis:', error);
        }
    };

    const handleAddUser = async () => {
        if (!selectedUser) return;

        try {
            setAdding(true);

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/departments/register-user`,
                {
                    department_id: department.id,
                    user_id: selectedUser.value
                },
                true
            );

            if (response.code === 200) {
                Swal.fire('Sucesso', 'Atendente adicionado ao departamento!', 'success');
                setSelectedUser(null);
                fetchDepartmentUsers();
                fetchAvailableUsers();
                onSave();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveUser = async (user) => {
        const result = await Swal.fire({
            title: 'Remover atendente?',
            text: `Deseja remover "${user.name}" deste departamento?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await ApiService.send(
                "delete",
                `${process.env.REACT_APP_URL_API}video/departments/delete-user`,
                {
                    department_id: department.id,
                    user_id: user.id || user.user_id
                },
                true
            );

            if (response.code === 200) {
                Swal.fire('Removido', 'Atendente removido do departamento.', 'success');
                fetchDepartmentUsers();
                fetchAvailableUsers();
                onSave();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-users me-2"></i>
                    Atendentes - {department?.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Add User */}
                <div className="mb-4">
                    <h6 className="text-muted mb-3">Adicionar Atendente</h6>
                    <div className="d-flex gap-2">
                        <div className="flex-grow-1">
                            <Select
                                options={availableUsers.map(u => ({
                                    value: u.id || u.user_id,
                                    label: u.name
                                }))}
                                value={selectedUser}
                                onChange={setSelectedUser}
                                placeholder="Selecione um usuário..."
                                isClearable
                                noOptionsMessage={() => "Nenhum usuário disponível"}
                            />
                        </div>
                        <button
                            className="btn btn-success"
                            onClick={handleAddUser}
                            disabled={!selectedUser || adding}
                        >
                            {adding ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <><i className="fa fa-plus me-1"></i> Adicionar</>
                            )}
                        </button>
                    </div>
                </div>

                <hr />

                {/* Users List */}
                <div>
                    <h6 className="text-muted mb-3">
                        Atendentes do Departamento ({users.length})
                    </h6>

                    {loading ? (
                        <Skeleton count={3} height={50} />
                    ) : users.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th className="text-end">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id || user.user_id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="video-user-avatar me-2" style={{ width: '35px', height: '35px', fontSize: '0.9rem' }}>
                                                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                                    </div>
                                                    {user.name}
                                                </div>
                                            </td>
                                            <td className="text-muted">{user.email || '-'}</td>
                                            <td className="text-end">
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleRemoveUser(user)}
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <i className="fa fa-users fa-2x text-muted mb-2"></i>
                            <p className="text-muted mb-0">Nenhum atendente neste departamento</p>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={onHide}>
                    <i className="fa fa-times me-2"></i>
                    Fechar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default VideoDepartmentUsersModal;
